import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Api } from '../api'
import { ApiError, NO_RECV_FILTERS, USD, LOCAL, isConverted } from '../api/types'
import type {
  ApiFailure,
  Currency,
  RecvFilters,
  WeekComparison as WeekComparisonData,
  WeekDim,
  WeekMetric,
} from '../api/types'
import { EmptyState, Segmented, Loading, money, currencyNote } from './ui'
import { Explain } from './DevModeContext'
import { FailureState } from './FailureState'
import { ExportButtons } from './ExportButtons'
import type { ExportDoc, ExportRow } from '../export/model'
import { GridChrome, ResizeHandle, useGridLayout } from './gridLayout'
import { SortHeader, compareBy, useGridSort, useSortMirror, type SortState } from './gridSort'
import type { ReactNode } from 'react'
import { PARTIAL_NOTE, WITHHELD, partialOf } from '../api/answer'
import { Withheld, markPartial } from './Withheld'

const GRID_ID = 'receivables-weeks'

interface WeekRow {
  member: string; a: number | null; b: number | null; change: number | null; pct: number | null
  /* Under the new model a blank amount is withheld, which is not the same as a
     member absent from that week (M8): these say which, and partial names the
     sides ('a', 'b') and the member whose figure leaves withheld values out. */
  aWithheld: boolean; bWithheld: boolean; partial: Set<string>
}

/** Every heading sorts, the member column included — it sorts by the name shown. */
type WeekSortKey = 'member' | 'a' | 'b' | 'change' | 'pct'
/** The same five, as data, so a saved view's key can be checked against them. */
const WEEK_SORT_KEYS: string[] = ['member', 'a', 'b', 'change', 'pct']

interface WeekMeasure {
  key: string
  header: string
  /** The documented measure key, which is stable even when the header is a date. */
  explainKey: string
  width: number
  kind: 'money' | 'pct'
  cellClass: (r: WeekRow) => string
  cell: (r: WeekRow) => ReactNode
  value: (r: WeekRow) => number | null
  footClass: () => string
  foot: () => ReactNode
  footValue: () => number | null
}

/**
 * WEEK COMPARISON — one metric, two weeks, and the difference between them.
 *
 * ---------------------------------------------------------------------------
 * THE DIFFERENCE IS THE POINT, SO IT IS THE SORT ORDER
 *
 * Sorted by the size of the CHANGE, not by the amount. A team that collected
 * 4M both weeks is not news; one that collected 4M and then 400K is. Sorting by
 * amount would bury the second under the first.
 *
 * ---------------------------------------------------------------------------
 * A MEMBER MISSING FROM ONE WEEK IS NOT A ZERO
 *
 * It appeared in one week's results and not the other. For a FLOW metric
 * (collections, sales) that genuinely means nothing happened, so zero is right.
 * For an AS-OF metric (receivables, overdue) it means the member had no balance
 * at that instant, which is also zero. Both read as zero here — but the union
 * is taken deliberately rather than by joining on the shorter side, because
 * dropping a member that exists in only one week would hide the largest
 * movements there are: something appearing, or disappearing entirely.
 */

const METRICS: { value: WeekMetric; label: string }[] = [
  { value: 'collections', label: 'Collections' },
  { value: 'sales', label: 'Sales' },
  { value: 'receivables', label: 'Receivables' },
  { value: 'overdue', label: 'Overdue' },
]

const DIMS: { value: WeekDim; label: string }[] = [
  { value: 'Salesperson', label: 'Salesperson' },
  { value: 'BusinessTeam', label: 'Business Team' },
  { value: 'BusinessUnit', label: 'Business Unit' },
  { value: 'Company', label: 'Company' },
]

/** The Sunday of the week containing `d`, as yyyy-mm-dd. */
function weekOf(d: Date): string {
  const c = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  c.setUTCDate(c.getUTCDate() - c.getUTCDay())
  return c.toISOString().slice(0, 10)
}

function shiftWeeks(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n * 7)
  return d.toISOString().slice(0, 10)
}

function label(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  const end = new Date(d)
  end.setUTCDate(end.getUTCDate() + 6)
  const f = (x: Date) => x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  return `${f(d)} – ${f(end)}`
}

export function WeekComparison({
  api,
  viewAs = null,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
  filters = NO_RECV_FILTERS,
}: {
  api: Api
  viewAs?: string | null
  currency?: Currency
  /** The page's standing filter bar -- five of its twelve controls reach here,
      which is the intersection of what the four metrics' sources carry. */
  filters?: RecvFilters
}) {
  const thisWeek = weekOf(new Date())

  const [metric, setMetric] = useState<WeekMetric>('collections')
  const [dim, setDim] = useState<WeekDim>('BusinessTeam')
  const [weekA, setWeekA] = useState(thisWeek)
  const [weekB, setWeekB] = useState(shiftWeeks(thisWeek, -1))
  const [data, setData] = useState<WeekComparisonData | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)
  const memberRef = useRef<HTMLTableCellElement | null>(null)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  /* No initial key: this screen OPENS in its own ranking (biggest movers
     first), which is not any single column's order, so the natural state is
     "no column sorted" and the third click returns to it. */
  const { sort, setSort, toggle, ariaSort } = useGridSort<WeekSortKey>()
  /* THE SORT RIDES INSIDE A SAVED VIEW. An unrecognised key -- a column since
     dropped, or one this reader may not be shown -- falls back to this grid's
     own order rather than sorting by nothing. See useSortMirror. */
  useSortMirror(GRID_ID, sort, (s) => {
    setSort(s.key !== null && WEEK_SORT_KEYS.includes(s.key) ? (s as SortState<WeekSortKey>) : { key: null, desc: true })
  })

  // Sales has no business unit — the server refuses that combination and says
  // so. Move off it rather than letting the reader sit on an empty screen
  // wondering whether they have no data or no permission.
  const dimAvailable = !(metric === 'sales' && dim === 'BusinessUnit')

  /* THE SAME GAP, REACHED FROM THE FILTER BAR INSTEAD OF THE DIMENSION.
     The sales fact table records a company and a team and no business unit, so
     "sales for unit C2" has no column to answer it — and the snapshot's BU is
     NULL on every sales row for the same reason. The server refuses the
     combination with DENIED_NO_COLUMN whatever this says; said here, the reader
     gets a sentence instead of a refusal panel.

     NOT silently dropped from the request, which was the tempting fix: the
     control would still read "C2" while the figures covered every unit, and
     nothing on the screen would say so. The metric lives in this component and
     the filter on the page, so this is the only place that can see both. */
  const buFilterAvailable = !(metric === 'sales' && Boolean(filters.bu))
  const available = dimAvailable && buFilterAvailable

  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      const res = await api.receivablesWeeks({ metric, dim, weekA, weekB, currency, filters })
      setData(res.data)
      setV2(res.vis !== null)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      setLoading(false)
    }
  }, [api, metric, dim, weekA, weekB, currency, filters, viewAs])

  useEffect(() => {
    if (available) void load()
    else setLoading(false)
  }, [load, available])

  /** Union of both weeks' members, with the change between them. */
  const rows = useMemo(() => {
    if (!data) return []
    type Merged = Omit<WeekRow, 'change' | 'pct'>
    const map = new Map<string, Merged>()
    const marks = (m: { partial?: string | null }, side: 'a' | 'b') => {
      const p = partialOf(m)
      return [...(p.has('amount') ? [side] : []), ...(p.has('member') ? ['member'] : [])]
    }
    for (const m of data.weekA.members) {
      const k = m.member ?? '(blank)'
      map.set(k, { member: k, a: m.amount, b: null, aWithheld: v2 && m.amount === null, bWithheld: false, partial: new Set(marks(m, 'a')) })
    }
    for (const m of data.weekB.members) {
      const k = m.member ?? '(blank)'
      const e = map.get(k)
      if (e) {
        e.b = m.amount
        e.bWithheld = v2 && m.amount === null
        for (const x of marks(m, 'b')) e.partial.add(x)
      } else map.set(k, { member: k, a: null, b: m.amount, aWithheld: false, bWithheld: v2 && m.amount === null, partial: new Set(marks(m, 'b')) })
    }
    return [...map.values()]
      .map((r): WeekRow => {
        const a = r.a ?? 0
        const b = r.b ?? 0
        // Null means "not shown to you", which is not a change of zero. Only
        // treat absence-from-a-week as zero; treat a masked measure as unknown.
        // Under the new model a side is masked on its own (aWithheld, bWithheld).
        const masked = v2 ? r.aWithheld || r.bWithheld : r.a === null && r.b === null
        return { ...r, change: masked ? null : a - b, pct: masked ? null : b !== 0 ? (a - b) / Math.abs(b) : null }
      })
      .sort((x, y) => Math.abs(y.change ?? 0) - Math.abs(x.change ?? 0))
  }, [data, v2])

  /**
   * THE ORDER ON SCREEN — the reader's sort laid ON TOP of the ranking above,
   * never instead of it.
   *
   * `rows` is already ordered by the size of the change, which is the whole
   * point of this screen (see the header). So the sort is applied to a COPY and
   * only while a column is chosen: with no column chosen the ranking is handed
   * back untouched, which is also where the hook's third click returns to.
   *
   * The tie-break is the member name, ascending in both directions. Without it
   * a column of equal figures — four teams that collected nothing in either
   * week — would order itself by whichever week's members happened to be read
   * first, and a reader who sorted, looked away and sorted back would find the
   * rows moved for no visible reason.
   */
  const sortedRows = useMemo(() => {
    const k = sort.key
    if (!k) return rows
    const valueOf = (r: WeekRow) => (k === 'member' ? r.member : r[k])
    return [...rows].sort((x, y) =>
      compareBy(valueOf(x), valueOf(y), sort.desc, () => x.member.localeCompare(y.member)),
    )
  }, [rows, sort])

  /* A week's total is the sum of what shows. Under the new model a member's
     figure can be withheld while others show: the total leaves it out and is
     marked, and is withheld only when every member's is (M8). Under today's
     model nothing is withheld one member at a time, so this is the plain sum. */
  const side = (pick: (r: WeekRow) => number | null, withheld: (r: WeekRow) => boolean, key: 'a' | 'b') => {
    const seen = rows.filter((r) => !withheld(r))
    return {
      value: seen.length > 0 || rows.length === 0 ? seen.reduce((s, r) => s + (pick(r) ?? 0), 0) : null,
      partial: seen.length > 0 && (seen.length < rows.length || rows.some((r) => r.partial.has(key))),
    }
  }
  const sideA = side((r) => r.a, (r) => r.aWithheld, 'a')
  const sideB = side((r) => r.b, (r) => r.bWithheld, 'b')
  const totalA = sideA.value ?? 0
  const totalB = sideB.value ?? 0
  /* Under the new model the change is the sum of the changes that show: a member
     withheld in one week has no change to add, and the other week's amount would
     read as one (16c's review). Today's model keeps the difference of the totals. */
  const changeRows = v2 ? rows.filter((r) => r.change !== null) : rows
  const totalChange = v2 ? changeRows.reduce((s, r) => s + (r.change ?? 0), 0) : totalA - totalB
  const changeBase = v2 ? changeRows.reduce((s, r) => s + (r.b ?? 0), 0) : totalB
  const changeWithheld = v2 && rows.length > 0 && changeRows.length === 0
  const changePartial = v2 && changeRows.length > 0
    && (changeRows.length < rows.length || rows.some((r) => r.partial.has('a') || r.partial.has('b')))
  const rowWithheld = (r: WeekRow) => r.aWithheld || r.bWithheld
  const rowPartial = (r: WeekRow) => r.partial.has('a') || r.partial.has('b')
  /** Whether a column's figure is withheld on a row, or in the total: the extract says it in words (M8). */
  const withheldIn = (key: string, r?: WeekRow) =>
    key === 'a' ? (r ? r.aWithheld : sideA.value === null)
      : key === 'b' ? (r ? r.bWithheld : sideB.value === null)
      : r ? rowWithheld(r) : changeWithheld

  /**
   * THE FOUR MEASURE COLUMNS.
   *
   * Built here rather than at module scope because two of the headers are the
   * week labels themselves, which move every time the reader steps a week. The
   * KEYS are fixed (a, b, change, pct), and keys are what the layout stores —
   * the response carries exactly two sides, never N, so the column set is
   * permanent even though two of its names are not.
   *
   * The member column is not among them: it is the row's identity, it is a
   * <th scope="row">, and hiding it would leave a comparison of nothing.
   */
  const MEASURES: WeekMeasure[] = [
    {
      key: 'a', header: label(weekA), explainKey: 'thisWeek', width: 176, kind: 'money',
      cellClass: () => 'text-(--color-ink)',
      cell: (r) => (r.a === null ? (r.aWithheld ? <Withheld /> : '—') : markPartial(r.partial.has('a'), money(r.a))),
      value: (r) => r.a,
      footClass: () => 'text-(--color-ink)',
      foot: () => (sideA.value === null ? <Withheld /> : markPartial(sideA.partial, money(totalA))),
      footValue: () => totalA,
    },
    {
      key: 'b', header: label(weekB), explainKey: 'priorWeek', width: 176, kind: 'money',
      cellClass: () => 'text-(--color-ink-2)',
      cell: (r) => (r.b === null ? (r.bWithheld ? <Withheld /> : '—') : markPartial(r.partial.has('b'), money(r.b))),
      value: (r) => r.b,
      footClass: () => 'text-(--color-ink)',
      foot: () => (sideB.value === null ? <Withheld /> : markPartial(sideB.partial, money(totalB))),
      footValue: () => totalB,
    },
    {
      key: 'change', header: 'Change', explainKey: 'change', width: 160, kind: 'money',
      cellClass: (r) => `font-semibold ${tone(r.change, metric)}`,
      cell: (r) => (r.change === null ? (rowWithheld(r) ? <Withheld /> : '—') : markPartial(rowPartial(r), signed(r.change))),
      value: (r) => r.change,
      footClass: () => tone(totalChange, metric),
      foot: () => (changeWithheld ? <Withheld /> : markPartial(changePartial, signed(totalChange))),
      footValue: () => totalChange,
    },
    {
      key: 'pct', header: '%', explainKey: 'changePct', width: 96, kind: 'pct',
      cellClass: (r) => tone(r.change, metric),
      cell: (r) => (r.pct === null ? (rowWithheld(r) ? <Withheld /> : '—')
        : markPartial(rowPartial(r), `${r.pct > 0 ? '+' : ''}${(r.pct * 100).toFixed(0)}%`)),
      value: (r) => r.pct,
      footClass: () => tone(totalChange, metric),
      foot: () =>
        changeWithheld ? <Withheld />
          : changeBase !== 0 ? markPartial(changePartial, `${totalChange > 0 ? '+' : ''}${((totalChange / Math.abs(changeBase)) * 100).toFixed(0)}%`) : '—',
      footValue: () => (changeBase !== 0 ? totalChange / Math.abs(changeBase) : null),
    },
  ]

  /* The last hook. Nothing returns early any more (a failure is drawn in the
     grid's place, below), but if an early return is ever added it goes UNDER
     this line: a hook skipped on one render is a React error, and it turns the
     failure panel into the error boundary. That is how Material Planning hid
     its own failures until 20 Sep 2026. */
  const layout = useGridLayout(GRID_ID, MEASURES, memberRef)
  const shown = layout.columns

  /* THE EXTRACT IS THE SCREEN — the union of both weeks' members IN THE ORDER
     THEY ARE RENDERED (the change ranking, or whichever column the reader
     sorted by), the Total row last. Collections is stamped LOCAL whatever the
     toggle says — the source has no dollar figure — and the screen's caption
     travels as a context line.

     A failure refuses the extract outright. load() never clears `data`, so
     after a refused Salesperson request the previous grouping's figures are
     still held, and without this guard they would ship labelled "Salesperson". */
  const buildDoc = (): ExportDoc | null => {
    if (failure || loading || !available || rows.length === 0) return null
    const metricLabel = METRICS.find((m) => m.value === metric)?.label ?? metric
    const dimLabel = DIMS.find((d) => d.value === dim)?.label ?? dim
    const localOnly = metric === 'collections' && isConverted(currency)
    /* The extract states its own order, because it is read away from the
       screen that made it: a sheet whose rows are alphabetical while the line
       claims "largest first" is a sheet nobody can reconcile. */
    const sortLabel =
      sort.key === null
        ? null
        : sort.key === 'member'
          ? dimLabel
          : (MEASURES.find((c) => c.key === sort.key)?.header ?? sort.key)
    return {
      report: 'Receivables',
      page: 'Week Comparison',
      title: 'This week against another',
      subtitle: `${metricLabel} by ${dimLabel.toLowerCase()}, ${label(weekA)} against ${label(weekB)}.`,
      context: [
        `Measure: ${metricLabel}`,
        `Grouped by: ${dimLabel}`,
        `Weeks: ${label(weekA)} against ${label(weekB)} — Sunday to Saturday`,
        localOnly
          ? 'Shown in local currency: collections has no US dollar figure in the source.'
          : `Currency: ${currencyNote(currency)}`,
        sortLabel
          ? `Sorted by ${sortLabel}, ${sort.desc ? 'largest first' : 'smallest first'}.`
          : 'Sorted by the size of the change, largest first.',
      ],
      privileged: viewAs,
      currency: localOnly ? LOCAL : currency,
      tables: [
        {
          /* The extract is the screen: the member, then whichever measures are
             showing, in the reader's order. */
          columns: [{ header: dimLabel }, ...shown.map((c) => ({ header: c.header, kind: c.kind }))],
          rows: [
            ...sortedRows.map<ExportRow>((r) => ({
              cells: [r.member, ...shown.map((c) => (withheldIn(c.key, r) ? WITHHELD : c.value(r)))],
            })),
            {
              bold: true,
              cells: ['Total', ...shown.map((c) => (withheldIn(c.key) ? WITHHELD : c.footValue()))],
            } satisfies ExportRow,
          ],
          note: 'A member missing from one week reads as zero there; a masked measure reads as a dash. A dash in % means the compared week was zero.'
            + (v2 && (changePartial || rows.some((r) => r.partial.size > 0)) ? ` ${PARTIAL_NOTE}` : ''),
        },
      ],
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ---------------- Controls ---------------- */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-(--color-line) px-5 py-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <Explain surface="filters" measureKey="metric" className="eyebrow mb-1.5 block">
              Measure
            </Explain>
            <Segmented label="Metric" value={metric} onChange={setMetric} options={METRICS} />
          </div>
          <div>
            <Explain surface="filters" measureKey="dimension" className="eyebrow mb-1.5 block">
              Compare by
            </Explain>
            <Segmented label="Dimension" value={dim} onChange={setDim} options={DIMS} />
          </div>
          {/* Wraps on a phone, where the two pickers do not fit side by side;
              the "vs" then sits at the end of the first line. */}
          <div className="flex items-end gap-3 @max-3xl:flex-wrap">
            <WeekPicker label="This week" value={weekA} onChange={setWeekA} />
            <span className="pb-2 text-(--color-ink-4)">vs</span>
            <WeekPicker label="Compared with" value={weekB} onChange={setWeekB} />
          </div>
        </div>
        <div className="pb-0.5">
          <ExportButtons build={buildDoc} />
          <GridChrome api={layout} />
        </div>
      </div>

      {/* COLLECTIONS IS SERVED LOCALLY whatever the toggle says — the payments
          table has no rate and no dollar column. Matching the report this
          replaces, the figure is shown and captioned rather than withheld, so
          this line is the only thing separating it from a converted one. */}
      {metric === 'collections' && isConverted(currency) && (
        <p className="border-b border-(--color-line) bg-(--color-surface-2) px-5 py-2.5 text-(length:--text-small) font-semibold text-(--color-warn)">
          Shown in local currency: collections has no US dollar figure in the source.
        </p>
      )}

      {/* On EVERY fetch, not just the first — changing the metric, the grouping
          or either week refetches, and a 60% fade was too quiet a signal for a
          table whose numbers had just been replaced. See TrendChart for the
          full reasoning; the rule is the same everywhere. */}
      {loading && <Loading label="Loading both weeks…" />}

      {/* A FAILURE TAKES THE GRID'S PLACE, NOT THE WHOLE CARD. A refusal can be
          caused by one control choice: Compare by Salesperson, for a reader
          who may not see the salesperson column, is refused with
          DENIED_NO_COLUMN. When the panel replaced everything, the picker that
          would undo it went too, and the only way out was another tab.

          The unavailable combination is checked first because it sends no
          request: a failure still held from the previous choice is not the
          answer to this one. */}
      {!dimAvailable ? (
        <EmptyState
          title="Sales cannot be grouped by business unit"
          detail="The sales table records a company and a team but no business unit — so there is nothing to group on. Salesperson, Business Team and Company all work. The other three measures support all four."
        />
      ) : !buFilterAvailable ? (
        <EmptyState
          title="Sales cannot be filtered by business unit"
          detail="The same gap, reached from the filter bar: the sales table records a company and a team but no business unit, so there is nothing to filter on. Clear the Business unit filter, or choose Collections, Receivables or Overdue — all three carry it."
        />
      ) : failure ? (
        <FailureState failure={failure} onRetry={() => void load()} />
      ) : !data ? null : rows.length === 0 ? (
        <EmptyState
          title="Nothing in either week"
          detail="No activity in either period for the data you can see. This is an empty result, not a refusal."
        />
      ) : (
        <div className="scroll-slim min-h-0 flex-1 overflow-auto">
          <table className="report-table w-full min-w-max border-separate border-spacing-0 text-(length:--text-body)">
            <thead>
              <tr>
                {/* The member column is fixed furniture: always first, always
                    on screen, never in the layout — the pins measure past it.
                    It still SORTS, by the name shown in it: a reader looking
                    for one team in a long comparison should not have to hunt
                    down a list ordered by something else. */}
                <th
                  ref={memberRef}
                  scope="col"
                  aria-sort={ariaSort('member')}
                  className="sticky left-0 top-0 z-30 border-b border-(--color-line-strong) px-5 py-2.5 text-center font-semibold text-(--color-ink)"
                >
                  <SortHeader
                    label={DIMS.find((d) => d.value === dim)?.label ?? 'Member'}
                    active={sort.key === 'member'}
                    desc={sort.desc}
                    onToggle={() => toggle('member')}
                    surface="weeks"
                    measureKey="member"
                  />
                </th>
                {shown.map((c) => {
                  const pin = layout.pinOf(c.key)
                  const w = layout.widthOf(c.key) ?? c.width
                  return (
                    <th
                      key={c.key}
                      ref={layout.headerRef(c.key)}
                      scope="col"
                      aria-sort={ariaSort(c.key as WeekSortKey)}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(c.key) }}
                      onDragOver={(e) => { if (!dragKey || dragKey === c.key) return; e.preventDefault(); setOverKey(c.key) }}
                      onDrop={(e) => {
                        e.preventDefault()
                        if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                        setDragKey(null); setOverKey(null)
                      }}
                      onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                      style={{
                        width: w, minWidth: w, // a floor, not a cap: every column here is a figure (4 Oct 2026)
                        left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                        right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                      }}
                      className={`relative sticky top-0 border-b border-l border-(--color-line) px-4 py-2.5 text-center font-semibold text-(--color-ink) ${
                        pin ? 'z-30' : 'z-20'
                      } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
                    >
                      {/* SortHeader renders the data-head-label span itself,
                          with the explanation mark as a SIBLING of the sort
                          button rather than wrapped around it. */}
                      <SortHeader
                        label={c.header}
                        active={sort.key === c.key}
                        desc={sort.desc}
                        onToggle={() => toggle(c.key as WeekSortKey)}
                        surface="weeks"
                        measureKey={c.explainKey}
                        extra={{ Metric: metric, Week: c.header }}
                      />
                      <ResizeHandle
                        label={c.header}
                        widthOf={() => layout.headerWidth(c.key)}
                        minWidth={() => layout.minWidthOf(c.key)}
                        onResize={(px) => layout.resize(c.key, px)}
                        onAutoFit={() => layout.autoFit(c.key)}
                      />
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr
                  key={r.member}
                  className="group transition-colors hover:bg-(--color-navy-50)"
                >
                  <th
                    scope="row"
                    className="sticky left-0 z-[2] border-b border-(--color-line) bg-(--color-surface) px-5 py-2 text-left font-medium text-(--color-ink) group-hover:bg-(--color-navy-50)"
                  >
                    {markPartial(r.partial.has('member'), r.member)}
                  </th>
                  {shown.map((c) => {
                    const pin = layout.pinOf(c.key)
                    const w = layout.widthOf(c.key) ?? c.width
                    return (
                      <td
                        key={c.key}
                        style={{
                          width: w,
                          left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                          right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                        }}
                        className={`num whitespace-nowrap border-b border-l border-(--color-line) px-4 py-2 ${c.cellClass(r)} ${
                          pin ? 'sticky z-[1] bg-(--color-surface) group-hover:bg-(--color-navy-50)' : ''
                        }`}
                      >
                        {c.cell(r)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-(--color-line-strong) bg-(--color-surface-3) font-semibold">
                <th scope="row" className="sticky left-0 z-[3] bg-(--color-surface-3) px-5 py-2.5 text-left text-(--color-ink)">Total</th>
                {shown.map((c) => {
                  const pin = layout.pinOf(c.key)
                  const w = layout.widthOf(c.key) ?? c.width
                  return (
                    <td
                      key={c.key}
                      style={{
                        width: w,
                        left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                        right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                      }}
                      className={`num border-l border-(--color-line) px-4 py-2.5 ${c.footClass()} ${
                        pin ? 'sticky z-[2] bg-(--color-surface-3)' : ''
                      }`}
                    >
                      {c.foot()}
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * UP IS NOT ALWAYS GOOD, and this is the one place that judgement belongs.
 *
 * More collections and more sales is good. More receivables and more overdue is
 * the opposite. Colouring every rise green would quietly congratulate a team
 * whose overdue book had just grown.
 */
function tone(change: number | null, metric: WeekMetric): string {
  if (change === null || change === 0) return 'text-(--color-ink-3)'
  const goodWhenUp = metric === 'collections' || metric === 'sales'
  const good = change > 0 ? goodWhenUp : !goodWhenUp
  return good ? 'text-(--color-good)' : 'text-(--color-overdue)'
}

function signed(n: number): string {
  // A real minus sign (U+2212), which sets at digit width and lines up in a
  // column of figures where a hyphen does not.
  return n < 0 ? `−${money(Math.abs(n))}` : `+${money(n)}`
}

function WeekPicker({
  label: text,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      {/* The half-open range is the fact worth looking up: a payment on the
          boundary belongs to one week only, so the two halves of a comparison
          can never both claim it. */}
      <Explain surface="filters" measureKey="week" className="eyebrow mb-1.5 block">
        {text}
      </Explain>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(shiftWeeks(value, -1))}
          title="Previous week"
          className="h-8 w-8 rounded border border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) transition-colors hover:bg-(--color-surface-3)"
        >
          ‹
        </button>
        <span className="min-w-[130px] px-2 text-center text-(length:--text-base) font-medium text-(--color-ink)">
          {label(value)}
        </span>
        <button
          type="button"
          onClick={() => onChange(shiftWeeks(value, 1))}
          title="Next week"
          className="h-8 w-8 rounded border border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) transition-colors hover:bg-(--color-surface-3)"
        >
          ›
        </button>
      </div>
    </div>
  )
}
